import { createFileRoute, Link, useNavigate, stripSearchParams } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { motion } from "motion/react";
import { useEffect, useMemo, useState } from "react";
import { Search, ArrowUpRight, Clock, Sparkles, Flame, BookOpen } from "lucide-react";
import {
  ASK_ARIF_CATEGORIES,
  ASK_ARIF_ARTICLES,
  getCategory,
  type AskArifArticle,
} from "@/lib/askArif";
import { SECTION_OG, sectionImageMeta } from "@/lib/ogSections";
import { hreflangLinks } from "@/lib/hreflang";
import { pageSchemaScript, PERSON_ID } from "@/seo/schema";


const askArifSearchSchema = z.object({
  q: fallback(z.string(), "").default(""),
  cat: fallback(z.string(), "").default(""),
});

const ASK_TITLE = "Ask Arif — Knowledge Center on Security & Automation";
const ASK_DESC =
  "Practical insights on Security, Perimeter Protection, Smart Automation, Luxury Cinema and Intelligent Buildings — from 23+ years in the field.";

export const Route = createFileRoute("/ask-arif/")({
  validateSearch: zodValidator(askArifSearchSchema),
  search: { middlewares: [stripSearchParams({ q: "", cat: "" })] },
  component: AskArifHome,
  head: () => ({

    meta: [
      { title: ASK_TITLE },
      { name: "description", content: ASK_DESC },

      { property: "og:title", content: "Ask Arif — Knowledge Center" },
      { property: "og:description", content: "Practical answers on intelligent environments — from a 1000+ project career." },
      { property: "og:url", content: "https://arifkhanglobal.com/ask-arif" },
      { property: "og:type", content: "website" },
      ...sectionImageMeta(SECTION_OG.askArif, "Ask Arif — knowledge center on security, automation and AV"),
    ],

    links: [
      { rel: "canonical", href: "https://arifkhanglobal.com/ask-arif" },
      ...hreflangLinks("/ask-arif"),
      { rel: "alternate", type: "text/markdown", href: "https://arifkhanglobal.com/ask-arif.md", title: "Markdown mirror" },
    ],
    scripts: [
      pageSchemaScript(
        {
          path: "/ask-arif",
          type: "CollectionPage",
          name: ASK_TITLE,
          description: ASK_DESC,
          breadcrumb: [{ name: "Ask Arif", url: "/ask-arif" }],
          extra: { author: { "@id": PERSON_ID } },
        },
        [
          {
            "@type": "ItemList",
            "@id": "https://arifkhanglobal.com/ask-arif#list",
            name: "Ask Arif — Articles",
            numberOfItems: ASK_ARIF_ARTICLES.length,
            itemListElement: ASK_ARIF_ARTICLES.map((a, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `https://arifkhanglobal.com/ask-arif/${a.slug}`,
              name: a.question,
            })),
          },
        ],
      ),
    ],


  }),
});

const SEARCH_SUGGESTIONS = [
  "How much does home automation cost?",
  "What mistakes happen in home theatre projects?",
  "How does perimeter security work?",
];

function AskArifHome() {
  const { q, cat } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [query, setQuery] = useState(q);

  // Keep the local input in sync when the URL changes (back/forward, suggestion click).
  useEffect(() => setQuery(q), [q]);

  // Debounce URL writes so typing stays smooth.
  useEffect(() => {
    if (query === q) return;
    const t = setTimeout(() => {
      navigate({ search: (prev: { q: string; cat: string }) => ({ ...prev, q: query }), replace: true });
    }, 200);
    return () => clearTimeout(t);
  }, [query, q, navigate]);

  const setCat = (next: string) =>
    navigate({ search: (prev: { q: string; cat: string }) => ({ ...prev, cat: prev.cat === next ? "" : next }) });

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term && !cat) return null;
    return ASK_ARIF_ARTICLES.filter((a) => {
      if (cat && a.category !== cat) return false;
      if (!term) return true;
      return [a.question, a.shortAnswer, ...a.tags].join(" ").toLowerCase().includes(term);
    }).slice(0, 12);
  }, [q, cat]);

  const trending = ASK_ARIF_ARTICLES.filter((a) => a.trending).slice(0, 4);
  const popular = ASK_ARIF_ARTICLES.filter((a) => a.popular).slice(0, 4);
  const latest = [...ASK_ARIF_ARTICLES]
    .sort((a, b) => +new Date(b.publishedAt) - +new Date(a.publishedAt))
    .slice(0, 6);
  const recommended = ASK_ARIF_ARTICLES.filter((a) => a.recommended).slice(0, 3);

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border bg-surface pt-36 pb-24">
        <div aria-hidden className="absolute inset-0 grain pointer-events-none" />
        <div
          aria-hidden
          className="absolute -top-40 left-1/2 -translate-x-1/2 h-[640px] w-[1100px] rounded-full bg-gradient-to-br from-primary/15 via-accent/10 to-transparent blur-3xl"
        />
        <div className="container-page relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-3xl"
          >
            <span className="eyebrow text-primary">Knowledge Center</span>
            <h1 className="mt-5 text-balance text-5xl md:text-7xl font-semibold tracking-tight leading-[0.95]">
              Ask Arif<span className="text-primary">.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-pretty text-lg text-ink-soft leading-relaxed">
              Practical insights from 23+ years across Security Infrastructure, Smart Automation,
              Perimeter Protection, Luxury Home Cinema and Intelligent Environments.
            </p>
          </motion.div>

          {/* Search */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 max-w-3xl"
          >
            <div className="relative glass border border-white/40 rounded-2xl shadow-[0_30px_80px_-40px_rgba(21,101,192,0.45)]">
              <Search
                className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-ink-soft"
                strokeWidth={1.6}
              />
              <label htmlFor="ask-arif-search" className="sr-only">
                Search Ask Arif articles
              </label>
              <input
                id="ask-arif-search"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="What would you like to ask Arif?"
                className="w-full bg-transparent pl-14 pr-5 py-5 text-base md:text-lg outline-none placeholder:text-ink-soft"
              />
            </div>

            {/* Category filters */}
            <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Filter by category">
              <button
                type="button"
                onClick={() => navigate({ search: (prev: { q: string; cat: string }) => ({ ...prev, cat: "" }) })}
                aria-pressed={!cat}
                className={`tap-target text-xs font-medium px-3.5 py-1.5 rounded-full border transition-colors ${
                  !cat
                    ? "border-primary/50 bg-primary/10 text-primary"
                    : "border-border bg-background/70 text-ink-soft hover:text-primary hover:border-primary/40"
                }`}
              >
                All categories
              </button>
              {ASK_ARIF_CATEGORIES.map((c) => (
                <button
                  key={c.slug}
                  type="button"
                  onClick={() => setCat(c.slug)}
                  aria-pressed={cat === c.slug}
                  className={`tap-target text-xs font-medium px-3.5 py-1.5 rounded-full border transition-colors ${
                    cat === c.slug
                      ? "border-primary/50 bg-primary/10 text-primary"
                      : "border-border bg-background/70 text-ink-soft hover:text-primary hover:border-primary/40"
                  }`}
                >
                  {c.title}
                </button>
              ))}
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              {SEARCH_SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setQuery(s)}
                  className="tap-target text-xs font-medium px-3.5 py-1.5 rounded-full border border-dashed border-border bg-background/70 text-ink-soft hover:text-primary hover:border-primary/40 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>

            {results && (
              <div className="mt-6" aria-live="polite">
                <p className="text-xs uppercase tracking-wider text-ink-soft">
                  {results.length} {results.length === 1 ? "answer" : "answers"}
                  {cat ? ` in ${getCategory(cat)?.title ?? cat}` : ""}
                </p>
                <div className="mt-3 grid gap-2">
                  {results.length === 0 ? (
                    <p className="text-sm text-ink-soft">
                      No results yet — try a different phrasing or clear the category filter.
                    </p>
                  ) : (
                    results.map((a) => (
                      <Link
                        key={a.slug}
                        to="/ask-arif/$slug"
                        params={{ slug: a.slug }}
                        hash="short-answer"
                        className="group flex items-start justify-between gap-4 rounded-xl border border-border bg-background px-4 py-3 hover:border-primary/50 transition-colors"
                      >
                        <span className="min-w-0">
                          <span className="block text-sm font-medium">{a.question}</span>
                          <span className="mt-1 block text-xs text-ink-soft line-clamp-2">
                            {a.shortAnswer}
                          </span>
                          <span className="mt-1.5 block text-[11px] font-medium text-primary">
                            Jump to short answer
                          </span>
                        </span>
                        <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 text-ink-soft group-hover:text-primary transition-colors" />
                      </Link>
                    ))
                  )}
                </div>
              </div>
            )}
          </motion.div>
        </div>

      </section>

      {/* Categories */}
      <section className="py-24 border-b border-border">
        <div className="container-page">
          <div className="flex items-end justify-between gap-6 mb-12">
            <div>
              <span className="eyebrow">Knowledge Categories</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight">
                Browse by domain.
              </h2>
            </div>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {ASK_ARIF_CATEGORIES.map((c, i) => (
              <motion.div
                key={c.slug}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.04, ease: [0.16, 1, 0.3, 1] }}
              >
                <Link
                  to="/ask-arif/category/$category"
                  params={{ category: c.slug }}
                  className="group relative block h-full overflow-hidden rounded-2xl border border-border bg-background p-6 hover:border-primary/40 hover:shadow-[0_20px_60px_-30px_rgba(21,101,192,0.35)] transition-all"
                >
                  <div
                    aria-hidden
                    className={`absolute inset-0 bg-gradient-to-br ${c.accent} opacity-0 group-hover:opacity-100 transition-opacity`}
                  />
                  <div className="relative">
                    <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <c.Icon className="h-5 w-5" strokeWidth={1.6} />
                    </div>
                    <h3 className="mt-5 text-base font-semibold tracking-tight">{c.title}</h3>
                    <p className="mt-2 text-sm text-ink-soft leading-relaxed">{c.short}</p>
                    <div className="mt-5 inline-flex items-center gap-1.5 text-xs font-medium text-primary">
                      Explore
                      <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Most Popular Questions */}
      <DynamicRow
        eyebrow="Most Popular Questions"
        title="The questions owners ask most."
        Icon={Flame}
        items={popular}
      />

      {/* Trending Topics */}
      <DynamicRow
        eyebrow="Trending Topics"
        title="What's moving in intelligent environments."
        Icon={Sparkles}
        items={trending}
        muted
      />

      {/* Latest Insights */}
      <section className="py-24 border-b border-border">
        <div className="container-page">
          <div className="flex items-end justify-between gap-6 mb-10">
            <div>
              <span className="eyebrow text-primary">Latest Insights</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight">
                Recently published.
              </h2>
            </div>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {latest.map((a, i) => (
              <ArticleCard key={a.slug} article={a} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Recommended Reading */}
      <section className="py-24 bg-surface">
        <div className="container-page">
          <div className="flex items-end justify-between gap-6 mb-10">
            <div>
              <span className="eyebrow">Recommended Reading</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight flex items-center gap-3">
                <BookOpen className="h-7 w-7 text-primary" strokeWidth={1.6} />
                Curated by Arif.
              </h2>
            </div>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {recommended.map((a, i) => (
              <ArticleCard key={a.slug} article={a} index={i} variant="feature" />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function DynamicRow({
  eyebrow,
  title,
  Icon,
  items,
  muted,
}: {
  eyebrow: string;
  title: string;
  Icon: typeof Flame;
  items: AskArifArticle[];
  muted?: boolean;
}) {
  return (
    <section className={`py-24 border-b border-border ${muted ? "bg-surface" : ""}`}>
      <div className="container-page">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <span className="eyebrow">{eyebrow}</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight flex items-center gap-3">
              <Icon className="h-7 w-7 text-primary" strokeWidth={1.6} />
              {title}
            </h2>
          </div>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {items.map((a, i) => (
            <ArticleCard key={a.slug} article={a} index={i} compact />
          ))}
        </div>
      </div>
    </section>
  );
}

function ArticleCard({
  article,
  index,
  compact,
  variant,
}: {
  article: AskArifArticle;
  index: number;
  compact?: boolean;
  variant?: "feature";
}) {
  const cat = getCategory(article.category);
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
    >
      <Link
        to="/ask-arif/$slug"
        params={{ slug: article.slug }}
        className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-background p-6 hover:border-primary/40 hover:shadow-[0_20px_60px_-30px_rgba(21,101,192,0.35)] transition-all ${
          variant === "feature" ? "md:p-8" : ""
        }`}
      >
        {cat && (
          <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-widest text-primary">
            <cat.Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
            {cat.title}
          </div>
        )}
        <h3
          className={`mt-4 font-semibold tracking-tight text-foreground ${
            variant === "feature" ? "text-xl md:text-2xl" : compact ? "text-base" : "text-lg"
          }`}
        >
          {article.question}
        </h3>
        {!compact && (
          <p className="mt-3 text-sm text-ink-soft leading-relaxed line-clamp-3">
            {article.shortAnswer}
          </p>
        )}
        <div className="mt-auto pt-6 flex items-center justify-between text-[11px] text-ink-soft">
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3 w-3" strokeWidth={1.8} />
            {article.readingMinutes} min read
          </span>
          <ArrowUpRight className="h-4 w-4 text-ink-soft group-hover:text-primary transition-colors" />
        </div>
      </Link>
    </motion.div>
  );
}
